import { AvailableInsuredDto } from 'api/borrower';
import { Sizes } from 'enums/sizes';
import { FC, useCallback, useEffect, useState } from 'react';
import { borrowersAPI } from 'services/swagger-api';
import { Button } from 'components/ui/Button';
import { useDialog } from 'hooks/useDialog';
import { GenericLoading } from 'components/ui/Loader';
import { Users, Percent, Euro, User } from 'lucide-react';
import { CheckIcon } from 'components/icons/Icons';

interface GroupSelectionDialogContentProps {
  borrowerProjectId: string;
  borrowerId: string;
  onConfirm: (borrowerIds: string[]) => void;
}

export const GroupSelectionDialogContent: FC<GroupSelectionDialogContentProps> = ({
  borrowerProjectId,
  borrowerId,
  onConfirm
}) => {
  const { closeDialog, setDialogActions } = useDialog();
  const [isLoading, setIsLoading] = useState(true);
  const [availableInsureds, setAvailableInsureds] = useState<AvailableInsuredDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedInsuredId, setSelectedInsuredId] = useState<string | null>(null);

  const fetchGroupSelection = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await borrowersAPI.getGroupSelection({
        borrowerProjectId,
        currentEmprunteurId: borrowerId
      });
      setAvailableInsureds(response.availableInsureds || []);
    } catch (err) {
      setError('Erreur lors de la récupération des données de groupe');
      console.error('Error fetching group selection:', err);
    } finally {
      setIsLoading(false);
    }
  }, [borrowerProjectId, borrowerId]);

  useEffect(() => {
    fetchGroupSelection();
  }, [fetchGroupSelection]);

  useEffect(() => {
    setDialogActions({
      closeLabel: 'Annuler',
      submitLabel: 'Confirmer la sélection',
      submitDisabled: isLoading || !selectedInsuredId,
      onSubmit: () => {
        if (selectedInsuredId) {
          onConfirm([borrowerId, selectedInsuredId]);
        }
        closeDialog();
      }
    });
  }, [setDialogActions, isLoading, selectedInsuredId, borrowerId, onConfirm, closeDialog]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <GenericLoading loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        <p className="text-red-500">{error}</p>
        <Button outline color="primary" onClick={fetchGroupSelection}>
          Réessayer
        </Button>
      </div>
    );
  }

  if (availableInsureds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        <Users className="w-12 h-12 text-gray-400" />
        <p className="text-gray-500 text-center">
          Aucun autre assuré disponible pour une tarification de groupe
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
        <Users className="w-4 h-4" />
        <span>Assurés disponibles pour une tarification de groupe</span>
      </div>

      <div className="flex flex-col gap-3">
        {availableInsureds.map((insured) => {
          const isSelected = selectedInsuredId === insured.borrowerId;
          return (
            <div
              key={insured.borrowerId}
              className={`group relative flex items-center gap-4 p-4 rounded-xl border-2 w-full cursor-pointer transition-all duration-200 ${
                isSelected
                  ? 'bg-green-50 border-green-500 shadow-md shadow-green-100'
                  : 'bg-white border-gray-200 hover:border-primary/50 hover:shadow-sm'
              }`}
              onClick={() => setSelectedInsuredId(insured.borrowerId)}
            >
              {/* User icon */}
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                  isSelected
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-500 group-hover:bg-primary/10 group-hover:text-primary'
                }`}
              >
                <User className="w-6 h-6" />
              </div>

              {/* Insured info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`text-sm font-bold tracking-wide ${
                      isSelected ? 'text-green-700' : 'text-gray-800'
                    }`}
                  >
                    {insured.firstName} {insured.name}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Euro className="w-3 h-3" />
                    Individuel: {insured.individualPrice.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </span>
                  <span className="flex items-center gap-1 text-green-600 font-medium">
                    <Percent className="w-3 h-3" />
                    -{insured.discountPercentage.toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="flex flex-col items-end gap-1">
                <span
                  className={`text-lg font-bold ${
                    isSelected ? 'text-green-600' : 'text-primary-600'
                  }`}
                >
                  {insured.groupPrice.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
                <span className="text-xs text-green-600">
                  (-{insured.discount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €)
                </span>
              </div>

              {/* Selection indicator */}
              <div className="flex-shrink-0">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected
                      ? 'bg-green-500 border-green-500'
                      : 'border-gray-300 group-hover:border-primary/50'
                  }`}
                >
                  {isSelected && <CheckIcon className="w-4 h-4 text-white" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-700">
          En sélectionnant ce tarif, vous bénéficierez d'une réduction groupe sur l'ensemble des assurés.
        </p>
      </div>
    </div>
  );
};

export const GROUP_SELECTION_DIALOG_CONFIG = {
  title: 'Tarification de groupe',
  size: Sizes.xl
};
